import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import contactApi from '../../api/contactApi';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    loading: true,
    token: localStorage.getItem('token'),
  });

  const setAuthToken = useCallback((token) => {
    if (token) {
      axios.defaults.headers.common['x-auth-token'] = token;
      contactApi.setAuthToken(token); // Set token in our API client
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['x-auth-token'];
      contactApi.setAuthToken(null); // Clear token from our API client
      localStorage.removeItem('token');
    }
  }, []);

  const loadUser = async () => {
    if (localStorage.token) {
      setAuthToken(localStorage.token);
    }

    try {
      const res = await axios.get('/api/auth/me');
      setAuthState({
        isAuthenticated: true,
        user: res.data,
        loading: false,
        token: localStorage.token,
      });
    } catch (err) {
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        token: null,
      });
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async (email, password) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const body = JSON.stringify({ email, password });

    try {
      const res = await axios.post('/api/auth/login', body, config);
      setAuthToken(res.data.token);
      await loadUser();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.msg || 'Login failed',
      };
    }
  };

  const register = async (formData) => {
    try {
      // Don't set Content-Type manually — axios sets it automatically for
      // FormData with the correct multipart boundary.
      const res = await axios.post('/api/auth/register', formData);
      setAuthToken(res.data.token);
      await loadUser();
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.msg || 'Registration failed',
        errors: err.response?.data?.errors,
      };
    }
  };

  const logout = () => {
    setAuthToken(null);
    setAuthState({
      isAuthenticated: false,
      user: null,
      loading: false,
      token: null,
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        register,
        logout,
        loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export { AuthContext };
